import React from 'react'
import { Typography, Chip, CircularProgress, IconButton, Tooltip } from '@material-ui/core'
import { ExpandMore, ExpandLess, ZoomIn, ZoomOut, ZoomOutMap, Fullscreen, CropFree } from '@material-ui/icons'

const ZOOM_MIN = 1
const ZOOM_MAX = 5
const ZOOM_STEP = 0.1

const normalizeUnit = (value) => {
  const upper = String(value || '').trim().toUpperCase()
  if (!upper) return ''
  if (upper === 'S.F.' || upper === 'SF') return 'SF'
  if (upper === 'L.F.' || upper === 'LF') return 'LF'
  if (upper === 'GAL.' || upper === 'GALLON' || upper === 'GAL') return 'GAL'
  return upper
}

const buildSecondaryQuantities = (row) => {
  if (!row) return []
  const primaryQty = row.quantity != null ? row.quantity : row.qty
  const primaryUnit = normalizeUnit(row.unit || (row.size && primaryQty != null ? 'EA' : ''))
  const text = `${row.comments || ''} ${row.sourceWords || ''}`
  const results = []
  const seen = new Set()
  const re = /(\d[\d,]*(?:\.\d+)?)\s*(SF|LF|CY|TON|EA|GAL)\b/ig
  let match
  while ((match = re.exec(text)) !== null) {
    const qty = Number(String(match[1]).replace(/,/g, ''))
    const unit = normalizeUnit(match[2])
    if (!Number.isFinite(qty) || !unit) continue
    if (primaryQty != null && Number(primaryQty) === qty && (!primaryUnit || primaryUnit === unit)) continue
    const key = `${qty}:${unit}`
    if (seen.has(key)) continue
    seen.add(key)
    results.push({ qty, unit })
  }
  return results
}

const SourceCropPanel = ({
  baseUrl,
  projectId,
  docId,
  sheetIndex,
  pageNumber,
  tableIndex,
  reviewArtifacts,
  artifactIndex = 0,
  row,
  rowIndex = 0,
  onApplySecondaryQuantity
}) => {
  const [imgLoaded, setImgLoaded] = React.useState(false)
  const [imgError, setImgError] = React.useState(false)
  const [showSource, setShowSource] = React.useState(false)
  const [zoom, setZoom] = React.useState(1)
  const containerRef = React.useRef(null)
  const [naturalSize, setNaturalSize] = React.useState({ width: 0, height: 0 })
  const [fullPageUrl, setFullPageUrl] = React.useState('')
  const [showFullPage, setShowFullPage] = React.useState(false)

  const artifact = Array.isArray(reviewArtifacts) && reviewArtifacts[artifactIndex]
  const sourcePageNumber = Number((artifact && artifact.page) || pageNumber || 0)
  if (!artifact && !sourcePageNumber) return null

  const rowSourceText = String(row?.sourceWords || row?.comments || '').trim()
  const displaySourceText = rowSourceText || (artifact && artifact.sourceText) || ''
  const primaryQty = row ? (row.quantity != null ? row.quantity : row.qty) : null
  const primaryUnit = normalizeUnit(row?.unit || ((row?.size || row?.botanicalName || row?.commonName) && primaryQty != null ? 'EA' : ''))
  const secondaryQuantities = buildSecondaryQuantities(row)
  const pageNumberParam = pageNumber ? `&pageNumber=${pageNumber}` : ''
  const cropUrl = artifact
    ? `${baseUrl}/api/bid-estimation/projects/${projectId}/documents/${docId}/sheets/${sheetIndex}/tables/${tableIndex}/crop-preview?artifactIndex=${artifactIndex}&pad=12${pageNumberParam}`
    : ''
  const rowEvidenceUrl = artifact && row && row.bbox
    ? `${baseUrl}/api/bid-estimation/projects/${projectId}/documents/${docId}/sheets/${sheetIndex}/tables/${tableIndex}/crop-preview?artifactIndex=${artifactIndex}&pad=12${pageNumberParam}&rowIndex=${rowIndex}`
    : ''
  const imageUrl = (showFullPage && fullPageUrl) ? fullPageUrl : (artifact ? cropUrl : fullPageUrl)
  const [showLineEvidence, setShowLineEvidence] = React.useState(Boolean(rowEvidenceUrl))

  // Reset loading state and zoom when URL or view mode changes
  React.useEffect(() => {
    setImgLoaded(false)
    setImgError(false)
    setNaturalSize({ width: 0, height: 0 })
  }, [cropUrl, fullPageUrl, showFullPage])

  // Reset to crop view when page/table changes
  React.useEffect(() => {
    setShowFullPage(false)
  }, [sheetIndex, tableIndex, pageNumber])

  React.useEffect(() => {
    setShowLineEvidence(Boolean(rowEvidenceUrl))
  }, [rowEvidenceUrl])

  React.useEffect(() => {
    let cancelled = false

    if (!sourcePageNumber) {
      setFullPageUrl('')
      return undefined
    }

    setFullPageUrl('')
    fetch(`${baseUrl}/api/bid-estimation/projects/${projectId}/documents/${docId}/page-images`, {
      credentials: 'include'
    })
      .then((res) => res.json())
      .then((payload) => {
        if (cancelled) return
        const pages = Array.isArray(payload?.pages) ? payload.pages : []
        const match = pages.find((entry) => Number(entry.page) === sourcePageNumber)
        setFullPageUrl(match && match.url ? match.url : '')
      })
      .catch(() => {
        if (!cancelled) setFullPageUrl('')
      })

    return () => {
      cancelled = true
    }
  }, [sourcePageNumber, baseUrl, projectId, docId])

  // Mouse wheel zoom
  const handleWheel = React.useCallback((e) => {
    if (!e.ctrlKey && !e.metaKey) return
    e.preventDefault()
    setZoom(prev => {
      const next = prev + (e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP)
      return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, next))
    })
  }, [])

  React.useEffect(() => {
    const el = containerRef.current
    if (!el) return
    el.addEventListener('wheel', handleWheel, { passive: false })
    return () => el.removeEventListener('wheel', handleWheel)
  }, [handleWheel])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#f8f8f8' }}>
      {/* Header */}
      <div style={{
        padding: '8px 12px',
        background: '#fff',
        borderBottom: '1px solid #eee',
        display: 'flex',
        alignItems: 'center',
        gap: 6
      }}>
        <Typography variant="subtitle2" style={{ fontWeight: 600, color: '#1565c0' }}>
          Source: Page {sourcePageNumber}
        </Typography>
        {row && (
          <Chip
            size="small"
            label={`Row ${rowIndex + 1}`}
            style={{
              backgroundColor: '#e8f5e9',
              color: '#2e7d32',
              height: 20,
              fontSize: 10
            }}
          />
        )}
        {artifact && artifact.label && !showFullPage && (
          <Chip
            size="small"
            label={artifact.label}
            style={{
              backgroundColor: '#e3f2fd',
              color: '#1565c0',
              height: 20,
              fontSize: 10
            }}
          />
        )}
        {(!artifact || showFullPage) && (
          <Chip
            size="small"
            label="Full page"
            style={{
              backgroundColor: '#f5f5f5',
              color: '#616161',
              height: 20,
              fontSize: 10
            }}
          />
        )}
        {/* Full page toggle + Zoom controls */}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
          {artifact && (
            <Tooltip title={showFullPage ? 'Show cropped view' : 'Show full page'}>
              <IconButton
                size="small"
                onClick={() => { setShowFullPage(v => !v); setZoom(1) }}
                style={{ padding: 4, marginRight: 4 }}
              >
                {showFullPage
                  ? <CropFree fontSize="small" style={{ color: '#1565c0' }} />
                  : <Fullscreen fontSize="small" style={{ color: '#616161' }} />
                }
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Zoom out">
            <span>
              <IconButton
                size="small"
                onClick={() => setZoom(prev => Math.max(ZOOM_MIN, prev - ZOOM_STEP))}
                disabled={zoom <= ZOOM_MIN}
                style={{ padding: 4 }}
              >
                <ZoomOut fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Typography
            variant="caption"
            style={{ minWidth: 36, textAlign: 'center', color: zoom === 1 ? '#999' : '#1565c0', fontWeight: zoom === 1 ? 400 : 600 }}
          >
            {Math.round(zoom * 100)}%
          </Typography>
          <Tooltip title="Zoom in">
            <span>
              <IconButton
                size="small"
                onClick={() => setZoom(prev => Math.min(ZOOM_MAX, prev + ZOOM_STEP))}
                disabled={zoom >= ZOOM_MAX}
                style={{ padding: 4 }}
              >
                <ZoomIn fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          {zoom !== 1 && (
            <Tooltip title="Reset zoom">
              <IconButton size="small" onClick={() => setZoom(1)} style={{ padding: 4 }}>
                <ZoomOutMap fontSize="small" style={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </div>

      {row && (
        <div style={{
          padding: '8px 12px',
          background: '#fff',
          borderBottom: '1px solid #eee',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          flexWrap: 'wrap'
        }}>
          {primaryQty != null && primaryUnit && (
            <Chip
              size="small"
              label={`Primary: ${primaryQty} ${primaryUnit}`}
              style={{ backgroundColor: '#e3f2fd', color: '#1565c0', height: 20, fontSize: 10 }}
            />
          )}
          {secondaryQuantities.map((entry) => (
            <Chip
              key={`${entry.qty}-${entry.unit}`}
              size="small"
              label={`Alt: ${entry.qty} ${entry.unit}`}
              onClick={onApplySecondaryQuantity ? () => onApplySecondaryQuantity(rowIndex, entry.qty, entry.unit) : undefined}
              clickable={Boolean(onApplySecondaryQuantity)}
              style={{ backgroundColor: '#fff8e1', color: '#f57f17', height: 20, fontSize: 10 }}
            />
          ))}
        </div>
      )}

      {/* Crop image with zoom */}
      <div
        ref={containerRef}
        style={{
          flex: 1,
          padding: 8,
          overflow: 'auto',
          minHeight: 200,
          cursor: zoom > 1 ? 'grab' : 'default',
          textAlign: zoom === 1 ? 'center' : 'left'
        }}
      >
        {!imgLoaded && !imgError && imageUrl && (
          <CircularProgress size={32} />
        )}
        {imgError && (
          <Typography variant="body2" color="textSecondary">
            Could not load source preview
          </Typography>
        )}
        {!imgError && !imageUrl && (
          <Typography variant="body2" color="textSecondary">
            No preview available for page {sourcePageNumber}
          </Typography>
        )}
        {!imgError && imageUrl && (
          <div
            style={{
              display: imgLoaded ? 'inline-block' : 'none',
              width: zoom > 1 && naturalSize.width ? naturalSize.width * zoom : '100%',
              maxWidth: zoom === 1 ? '100%' : 'none',
              verticalAlign: 'top'
            }}
          >
            <img
              src={imageUrl}
              alt={`Source: ${artifact && artifact.label ? artifact.label : `page ${sourcePageNumber}`}`}
              onLoad={(e) => {
                setImgLoaded(true)
                setNaturalSize({
                  width: e.target.naturalWidth || 0,
                  height: e.target.naturalHeight || 0
                })
              }}
              onError={() => setImgError(true)}
              style={{
                maxWidth: '100%',
                width: zoom === 1 ? '100%' : `${zoom * 100}%`,
                objectFit: 'contain',
                display: 'block',
                borderRadius: 4,
                border: '1px solid #e0e0e0',
                transition: 'width 0.15s ease'
              }}
            />
          </div>
        )}
      </div>

      {/* Zoom hint */}
      {imgLoaded && (
        <div style={{
          padding: '2px 12px',
          fontSize: 10,
          color: '#bbb',
          background: '#fff',
          borderTop: '1px solid #f0f0f0',
          textAlign: 'right'
        }}>
          Ctrl + scroll to zoom
        </div>
      )}

      {rowEvidenceUrl && (
        <div style={{ borderTop: '1px solid #e0e0e0' }}>
          <div
            onClick={() => setShowLineEvidence(!showLineEvidence)}
            style={{
              padding: '6px 12px',
              background: '#fff',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              cursor: 'pointer',
              userSelect: 'none'
            }}
          >
            <IconButton size="small" style={{ padding: 2 }}>
              {showLineEvidence ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
            <Typography variant="caption" style={{ fontWeight: 600, color: '#555' }}>
              Line Item Evidence
            </Typography>
            <Typography variant="caption" color="textSecondary" style={{ marginLeft: 'auto' }}>
              {showLineEvidence ? 'click to collapse' : 'click to expand'}
            </Typography>
          </div>
          {showLineEvidence && (
            <div style={{
              padding: '8px 12px',
              background: '#fafafa',
              maxHeight: 180,
              overflow: 'auto'
            }}>
              <img
                src={rowEvidenceUrl}
                alt={`Line item evidence for row ${rowIndex + 1}`}
                style={{
                  width: '100%',
                  objectFit: 'contain',
                  display: 'block',
                  borderRadius: 4,
                  border: '1px solid #e0e0e0',
                  background: '#fff'
                }}
              />
            </div>
          )}
        </div>
      )}

      {/* Collapsible raw source text */}
      {displaySourceText && (
        <div style={{ borderTop: '1px solid #e0e0e0' }}>
          <div
            onClick={() => setShowSource(!showSource)}
            style={{
              padding: '6px 12px',
              background: '#fff',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              cursor: 'pointer',
              userSelect: 'none'
            }}
          >
            <IconButton size="small" style={{ padding: 2 }}>
              {showSource ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
            <Typography variant="caption" style={{ fontWeight: 600, color: '#555' }}>
              Raw Source Text
            </Typography>
            <Typography variant="caption" color="textSecondary" style={{ marginLeft: 'auto' }}>
              {showSource ? 'click to collapse' : 'click to expand'}
            </Typography>
          </div>
          {showSource && (
            <div style={{
              padding: '8px 12px',
              background: 'var(--df-ink, #1c2419)',
              color: 'var(--df-ink-4, #97a094)',
              fontFamily: '"Courier New", monospace',
              fontSize: 11,
              lineHeight: 1.6,
              maxHeight: 200,
              overflow: 'auto',
              userSelect: 'all',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}>
              {displaySourceText}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default SourceCropPanel
